import { useEffect, useMemo, useState } from "react";
import {
  Activity,
  ArrowDown,
  ArrowRight,
  ArrowUp,
  BrainCircuit,
  Gauge,
  ShieldCheck,
  Waves,
  X,
} from "lucide-react";
import { listSensorAnomalies } from "../api";
import type { SensorAnomaly, Vehicle } from "../types";

const readableSensor = (code: string) =>
  code.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());

export function AnomalyPanel({
  open,
  onClose,
  vehicles,
  onSelectVehicle,
}: {
  open: boolean;
  onClose: () => void;
  vehicles: Vehicle[];
  onSelectVehicle: (vehicle: Vehicle) => void;
}) {
  const [rows, setRows] = useState<SensorAnomaly[]>([]);
  const [selectedKey, setSelectedKey] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) return;
    let refreshPending = false;
    const refresh = () => {
      if (refreshPending) return;
      refreshPending = true;
      listSensorAnomalies()
        .then((items) => {
          setRows(items);
          setSelectedKey((current) =>
            items.some((item) => `${item.vehicle_id}:${item.sensor_code}` === current)
              ? current
              : items[0]
                ? `${items[0].vehicle_id}:${items[0].sensor_code}`
                : "",
          );
          setError("");
        })
        .catch((cause) =>
          setError(cause instanceof Error ? cause.message : "No fue posible cargar las anomalías"),
        )
        .finally(() => {
          refreshPending = false;
        });
    };
    refresh();
    window.addEventListener("gsf:anomaly-updated", refresh);
    return () => window.removeEventListener("gsf:anomaly-updated", refresh);
  }, [open]);

  const selected =
    rows.find((row) => `${row.vehicle_id}:${row.sensor_code}` === selectedKey) || rows[0];
  const selectedVehicle = vehicles.find((vehicle) => vehicle.id === selected?.vehicle_id);
  const summary = useMemo(
    () => ({
      critical: rows.filter((row) => row.anomaly_status === "critical").length,
      warning: rows.filter((row) => row.anomaly_status === "warning").length,
      vehicles: new Set(rows.map((row) => row.vehicle_id)).size,
    }),
    [rows],
  );
  const delta = selected ? selected.current_value - selected.baseline_median : 0;
  const deltaPercent =
    selected && selected.baseline_median
      ? (delta / Math.abs(selected.baseline_median)) * 100
      : 0;

  if (!open) return null;
  return (
    <div className="admin-overlay" role="presentation" onMouseDown={onClose}>
      <aside className="admin-panel anomaly-panel" role="dialog" aria-modal="true" aria-labelledby="anomaly-title" onMouseDown={(event) => event.stopPropagation()}>
        <header>
          <div>
            <span className="eyebrow">ANÁLISIS ROBUSTO · HISTORIAL PROPIO DEL SENSOR</span>
            <h2 id="anomaly-title">Detección de anomalías</h2>
          </div>
          <button type="button" onClick={onClose} aria-label="Cerrar anomalías"><X size={18} /></button>
        </header>
        <section className="admin-panel__summary">
          <span><Waves size={17} /><b>{summary.critical}</b> críticas</span>
          <span><Activity size={17} /><b>{summary.warning}</b> advertencias</span>
          <span><Gauge size={17} /><b>{summary.vehicles}</b> unidades afectadas</span>
        </section>
        <div className="anomaly-panel__body">
          <section className="anomaly-ranking">
            <div className="admin-section-title">
              <span className="eyebrow">DESVIACIONES ACTIVAS</span>
              <b>{rows.length} señales</b>
            </div>
            {rows.map((row) => {
              const vehicle = vehicles.find((item) => item.id === row.vehicle_id);
              const key = `${row.vehicle_id}:${row.sensor_code}`;
              return (
                <button
                  type="button"
                  className={`anomaly-item anomaly-item--${row.anomaly_status}${selectedKey === key ? " is-selected" : ""}`}
                  onClick={() => setSelectedKey(key)}
                  key={key}
                >
                  <span>{row.direction === "above" ? <ArrowUp size={15} /> : <ArrowDown size={15} />}</span>
                  <span>
                    <small>{vehicle?.unit_code || row.vehicle_id} · {row.component_code}</small>
                    <strong>{readableSensor(row.sensor_code)}</strong>
                  </span>
                  <b>{row.anomaly_score.toFixed(1)}σ</b>
                </button>
              );
            })}
            {!rows.length && <p className="maintenance-empty">SIN ANOMALÍAS CON BASE SUFICIENTE</p>}
          </section>
          <section className="anomaly-detail">
            {selected && (
              <>
                <div className={`anomaly-score anomaly-score--${selected.anomaly_status}`}>
                  <BrainCircuit size={25} />
                  <span><b>{selected.anomaly_score.toFixed(1)}σ</b><small>{selected.anomaly_status.toUpperCase()}</small></span>
                </div>
                <div className="anomaly-detail__title">
                  <span className="eyebrow">{selectedVehicle?.unit_code} · {selected.component_code}</span>
                  <h3>{readableSensor(selected.sensor_code)}</h3>
                  <p>{selectedVehicle?.make} {selectedVehicle?.model} · confianza {selected.confidence}</p>
                </div>
                <div className="anomaly-comparison">
                  <article><small>MEDIANA HISTÓRICA</small><strong>{selected.baseline_median.toLocaleString("es-MX")} <em>{selected.unit}</em></strong><span>{selected.sample_count} muestras</span></article>
                  <i>{selected.direction === "above" ? <ArrowUp size={18} /> : <ArrowDown size={18} />}</i>
                  <article><small>LECTURA ACTUAL</small><strong>{selected.current_value.toLocaleString("es-MX")} <em>{selected.unit}</em></strong><span>{deltaPercent > 0 ? "+" : ""}{deltaPercent.toFixed(1)}%</span></article>
                </div>
                <div className="anomaly-explanation">
                  <BrainCircuit size={18} />
                  <span><small>EXPLICACIÓN DEL MODELO</small><strong>{selected.explanation}</strong></span>
                </div>
                <div className="anomaly-recommendation">
                  <ShieldCheck size={18} />
                  <span><small>ACCIÓN RECOMENDADA</small><strong>{selected.recommendation}</strong></span>
                </div>
                <footer>
                  <small>Mediana/MAD · ventana máxima 30 muestras · {new Date(selected.captured_at).toLocaleString("es-MX")}</small>
                  {selectedVehicle && (
                    <button type="button" className="primary-button" onClick={() => { onSelectVehicle(selectedVehicle); onClose(); }}>
                      ABRIR UNIDAD <ArrowRight size={14} />
                    </button>
                  )}
                </footer>
              </>
            )}
          </section>
        </div>
        {error && <div className="admin-message admin-message--error">{error}</div>}
      </aside>
    </div>
  );
}
